/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useEffect, useCallback } from "react";
import { simpleFetch } from "../../restWrapper"; // Assuming this handles API calls
import "./RevenueShareConfig.css"; // Create this CSS file later
import PropTypes from "prop-types";

function RevenueShareConfig({ languageObj = {} }) {
  const [config, setConfig] = useState({
    revenueShareEnabled: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await simpleFetch(
        "/businesses/revenue-share/config",
        "GET",
      );
      // Check response.status instead of response.ok because simpleFetch returns a custom object
      if (response.status === 200) {
        // Access the body property of the custom response object
        const data = response.body;
        setConfig({
          revenueShareEnabled: data.revenueShareEnabled || false,
        });
      } else {
        // Handle potential non-JSON error responses
        let errorText = `Failed to fetch configuration. Status: ${response.status}`; // Default error
        try {
          // Try to access message from body if it exists (check body first)
          if (response.body && response.body.message) {
            errorText = response.body.message;
          }
        } catch (parseError) {
          // If accessing body fails, log the error
          // console.error("Failed to parse error response body:", parseError);
        }
        setError(errorText); // Set the error state
      }
    } catch (err) {
      // console.error("Fetch config error:", err); // Log the JS error
      setError("An error occurred while fetching configuration.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleToggleChange = (event) => {
    const checked = event && event.target ? event.target.checked : false;
    setConfig((prevConfig) => ({
      ...prevConfig,
      revenueShareEnabled: checked,
    }));
    // Clear success message on change
    setSuccessMessage("");
  };

  const handlePercentageChange = (event) => {
    const value = event.target.value;
    // Allow empty string or numbers between 0 and 100
    if (
      value === "" ||
      (/^\d*\.?\d*$/.test(value) &&
        parseFloat(value) >= 0 &&
        parseFloat(value) <= 100)
    ) {
      setConfig((prevConfig) => ({
        ...prevConfig,
        defaultDonationPercentage: value,
      }));
    }
    // Clear success message on change
    setSuccessMessage("");
  };

  // Removed handlePercentageBlur as defaultDonationPercentage is deprecated

  const handleSaveChanges = async () => {
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    // No defaultDonationPercentage to validate or save
    const payload = {
      revenueShareEnabled: config.revenueShareEnabled,
    };

    try {
      const response = await simpleFetch(
        "/businesses/revenue-share/config",
        "PUT",
        payload,
      );
      // Check response.status instead of response.ok
      if (response.status === 200) {
        setSuccessMessage("Configuration saved successfully!");
        // Optionally re-fetch config to confirm, though PUT should return updated state
        // fetchConfig();
      } else {
        // Access error message from response.body (check if body exists first)
        const errorMessage =
          response.body && response.body.message
            ? response.body.message
            : "Failed to save configuration.";
        setError(errorMessage);
      }
    } catch (err) {
      // console.error("Save config error:", err);
      setError("An error occurred while saving configuration.");
    } finally {
      setIsLoading(false);
      // Auto-clear success message after a few seconds
      if (successMessage) {
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    }
  };

  if (isLoading && !config.revenueShareEnabled) {
    // Show loading only on initial load
    return (
      <div className="revenue-share-loading">Loading configuration...</div>
    );
  }

  return (
    <div className="revenue-share-config-container">
      <h3>
        {languageObj.revenueShareTitle || "Revenue Sharing Configuration"}
      </h3>
      {error && <div className="revenue-share-error">{error}</div>}
      {successMessage && (
        <div className="revenue-share-success">{successMessage}</div>
      )}

      <div className="config-item">
        <label htmlFor="enableRevenueShare">
          {languageObj.enableSharingLabel || "Enable Revenue Sharing"}
        </label>
        <label className="switch">
          <input
            id="enableRevenueShare"
            type="checkbox"
            checked={config.revenueShareEnabled}
            onChange={handleToggleChange}
            disabled={isLoading}
          />
          <span className="slider round"></span>
        </label>
        <p className="config-description">
          {languageObj.enableSharingDesc ||
            "Allow a percentage of your sales to be donated to a chosen nonprofit."}
        </p>
      </div>

      <button
        onClick={handleSaveChanges}
        disabled={isLoading}
        className="save-button"
      >
        {isLoading
          ? languageObj.savingButton || "Saving..."
          : languageObj.saveButton || "Save Changes"}
      </button>
    </div>
  );
}

RevenueShareConfig.propTypes = {
  languageObj: PropTypes.object,
};

export default RevenueShareConfig;
